import {
  Navbar, Nav, Container, Dropdown, // Col,
} from 'react-bootstrap';
import React, { useEffect, useState, useContext } from 'react';
import { withRouter } from 'react-router';
import { NavLink, useLocation } from 'react-router-dom';
import styled, { ThemeContext } from 'styled-components';
import endpoints from '../constants/endpoints';
import Logo from './Logo';
import { useLanguageContext } from '../TranslateContext';
/* eslint-disable import/no-cycle */
import LanguageSelector from './LanguageSelector';
import './Navbar.css';

const ExternalNavLink = styled.a`
  color: ${(props) => props.theme.navbarTheme.linkColor};
  &:hover {
    color: ${(props) => props.theme.navbarTheme.linkHoverColor};
  }
  &::after {
    background-color: ${(props) => props.theme.accentColor};
  }
`;

const InternalNavLink = styled(NavLink)`
  color: ${(props) => props.theme.navbarTheme.linkColor};
  &:hover {
    color: ${(props) => props.theme.navbarTheme.linkHoverColor};
  }
  &::after {
    background-color: ${(props) => props.theme.accentColor};
  }
  &.navbar__link--active {
    color: ${(props) => props.theme.navbarTheme.linkActiveColor};
  }
`;

const Submenu = styled(Dropdown)`
`;

const SubmenuToggle = styled.a`
  color: ${(props) => props.theme.navbarTheme.linkColor};

  &:hover,
  &:focus,
  &:active,
  &.show {
    color: ${(props) => props.theme.navbarTheme.linkHoverColor};
  }

  &::before {
    color: inherit;
  }

  &:hover::before,
  &:focus::before,
  &.navbar__link--active::before {
    color: ${(props) => props.theme.accentColor};
  }

  &:hover .navbar__icon,
  &:focus .navbar__icon,
  &.navbar__link--active .navbar__icon {
    color: ${(props) => props.theme.accentColor};
  }

  &::after {
    background-color: ${(props) => props.theme.accentColor};
  }

  &.navbar__link--active {
    color: ${(props) => props.theme.navbarTheme.linkActiveColor};
  }
`;

const SubmenuMenu = styled(Dropdown.Menu)`
  border-color: ${(props) => props.theme.accentColor};
`;

const InternalSubmenuLink = styled(NavLink)`
  color: ${(props) => props.theme.navbarTheme.linkColor};

  &:hover,
  &:focus {
    color: ${(props) => props.theme.navbarTheme.linkHoverColor};
  }

  &:hover .navbar__icon,
  &:focus .navbar__icon,
  &.navbar__link--active .navbar__icon {
    color: ${(props) => props.theme.accentColor};
  }

  &.navbar__link--active {
    color: ${(props) => props.theme.navbarTheme.linkActiveColor};
  }
`;

const ExternalSubmenuLink = styled.a`
  color: ${(props) => props.theme.navbarTheme.linkColor};

  &:hover,
  &:focus {
    color: ${(props) => props.theme.navbarTheme.linkHoverColor};
  }

  &:hover .navbar__icon,
  &:focus .navbar__icon {
    color: ${(props) => props.theme.accentColor};
  }
`;

const navbarIcons = ['f015', 'f007', 'f013', 'f501', 'f0f2', 'f542', 'f10d', 'f559', 'f0e0'];

const navbarIconsByHref = {
  '/': 'f015',
  '/about': 'f007',
  '/background/skills': 'f013',
  '/background/education': 'f501',
  '/background/experience': 'f0f2',
  '/projects': 'f542',
  '/testimonials': 'f10d',
  '/credentials': 'f559',
  '/tools/encryption': 'f3ed',
  '/tools/hashing': '23',
  '/tools/signature': 'f5b7',
};

const getNavbarIcon = (href, fallback) => String.fromCharCode(
  parseInt(navbarIconsByHref[href] || fallback, 16),
);

const NavBar = () => {
  const { pathname } = useLocation();
  const theme = useContext(ThemeContext);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetch(endpoints.navbar, {
      method: 'GET',
    })
      .then((res) => res.json())
      .then((res) => setData(res))
      .catch((err) => err);
  }, [pathname]);

  const { i18n, onClickLanguageChange } = useLanguageContext();

  const closeNavbar = () => setExpanded(false);

  const renderLink = (section, exact = false, submenu = false) => {
    if (section?.type === 'link') {
      if (submenu) {
        return (
          <Dropdown.Item
            as={ExternalSubmenuLink}
            key={`${section.title}-${section.href}`}
            href={section.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeNavbar}
            theme={theme}
          >
            <span className="navbar__icon" aria-hidden="true">
              {getNavbarIcon(section.href)}
            </span>
            {section.title}
          </Dropdown.Item>
        );
      }

      return (
        <ExternalNavLink
          key={`${section.title}-${section.href}`}
          href={section.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={closeNavbar}
          theme={theme}
        >
          <span className="navbar__icon" aria-hidden="true">
            {getNavbarIcon(section.href)}
          </span>
          {section.title}
        </ExternalNavLink>
      );
    }

    if (submenu) {
      return (
        <Dropdown.Item
          as={InternalSubmenuLink}
          key={`${section.title}-${section.href}`}
          onClick={closeNavbar}
          exact={exact}
          className='navbar_submenu'
          activeClassName="navbar__link--active"
          to={section.href}
          theme={theme}
        >
          <span className="navbar__icon" aria-hidden="true">
            {getNavbarIcon(section.href)}
          </span>
          {section.title}
        </Dropdown.Item>
      );
    }

    return (
      <InternalNavLink
        key={`${section.title}-${section.href}`}
        onClick={closeNavbar}
        exact={exact}
        activeClassName="navbar__link--active"
        className="navbar__link"
        to={section.href}
        theme={theme}
      >
        <span className="navbar__icon" aria-hidden="true">
          {getNavbarIcon(section.href)}
        </span>
        {section.title}
      </InternalNavLink>
    );
  };

  const renderSection = (section, index) => {
    if (!section?.submenu) {
      return renderLink(section, index === 0);
    }

    const submenuIsActive = section.items?.some((item) => (
      item.href === '/'
        ? pathname === '/'
        : pathname === item.href || pathname.startsWith(`${item.href}/`)
    ));

    return (
      <Submenu
        key={`${section.title}-${section.href}`}
        className="navbar-submenu"
        theme={theme}
        // show={submenuIsActive}
      >
        <Dropdown.Toggle
          as={SubmenuToggle}
          id={`navbar-submenu-${index}`}
          className={`navbar__link${submenuIsActive ? ' navbar__link--active' : ''}`}
          theme={theme}
        >
          <span className="navbar__icon" aria-hidden="true">
            {getNavbarIcon(section.href, "f577")}
          </span>
          {section.title}
        </Dropdown.Toggle>
        <SubmenuMenu theme={theme}>
          {section.items?.map((item) => renderLink(item, item.href === '/', true))}
        </SubmenuMenu>
      </Submenu>
    );
  };

  return (
    <Navbar
      fixed="top"
      expand="xxl"
      bg="dark"
      variant="dark"
      className="navbar-custom"
      expanded={expanded}
    >
      <Container className="container-max-width">
        {data?.logo && (
          <Logo data={data} />
        )}
        <Navbar.Toggle
          aria-controls="responsive-navbar-nav"
          onClick={() => setExpanded(!expanded)}
        />
        <Navbar.Collapse id="responsive-navbar-nav">
          <Nav className="me-auto" />
          <Nav/* className="flex-row flex-wrap pt-2 py-md-0 text-center" */>
            {data
              && data.sections[i18n.language]?.map(renderSection)}
            <LanguageSelector
              available={['en', 'fr'/* , 'ru',  'pt' */]}
              onChangeEnd={(value) => onClickLanguageChange(value)}
            />
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

const NavBarWithRouter = withRouter(NavBar);
export default NavBarWithRouter;
